import React, { useMemo } from 'react';
import type { KaliaCalendarConfig, KaliaEvent, KaliaResource, PositionedEvent } from './types';
import { timeToPixel } from './useKaliaCalendarLayout';
import { buildEventContentArg } from './eventAdapter';
import KaliaCalendarEvent from './KaliaCalendarEvent';
import EventPopover from '~/components/treatment/appointment/calendar/popover/EventPopover';

type Props = {
    resource: KaliaResource;
    foregroundEvents: PositionedEvent[];
    backgroundEvents: KaliaEvent[];
    config: KaliaCalendarConfig;
    columnWidth: number;
    totalHeight: number;
    totalSlots: number;
    dayStartMinutes: number;
    allResources: KaliaResource[];
};

function getEventMinutes(date: Date, timezone: string): number {
    const formatted = date.toLocaleString('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    const [h, m] = formatted.split(':').map(Number);
    return h * 60 + m;
}

function KaliaCalendarColumn({
    resource,
    foregroundEvents,
    backgroundEvents,
    config,
    columnWidth,
    totalHeight,
    totalSlots,
    dayStartMinutes,
    allResources,
}: Props) {
    const bgBands = useMemo(() => {
        const dayEnd = dayStartMinutes + totalSlots * config.slotDuration;
        return backgroundEvents
            .filter((ev) => ev.start && ev.end)
            .map((ev) => {
                const startMin = getEventMinutes(ev.start, config.timezone);
                const endMin = getEventMinutes(ev.end, config.timezone);
                const clampedStart = Math.max(startMin, dayStartMinutes);
                const clampedEnd = Math.min(endMin, dayEnd);
                if (clampedStart >= clampedEnd) return null;

                const top = timeToPixel(clampedStart, dayStartMinutes, config.slotDuration, config.slotHeight);
                const height = timeToPixel(clampedEnd, dayStartMinutes, config.slotDuration, config.slotHeight) - top;

                const isAvailable = ev.metadata?.type === 'AVAILABLE';

                const bgStyle: React.CSSProperties = isAvailable
                    ? { backgroundColor: ev.color || '#8fdf82', opacity: 0.3 }
                    : { backgroundColor: ev.color || '#e0e7ff', opacity: 0.3 };

                const info = buildEventContentArg(ev, allResources);

                return (
                    <div
                        key={ev.id + '-' + resource.id}
                        className='absolute left-0 w-full z-1'
                        style={{ top, height, ...bgStyle }}
                    >
                        <EventPopover info={info} />
                    </div>
                );
            })
            .filter(Boolean);
    }, [backgroundEvents, config, dayStartMinutes, totalSlots, resource.id, allResources]);

    const BODY_TOP_OFFSET = 9;

    return (
        <div
            className='shrink-0 border-r border-gray-300'
            style={{ height: totalHeight + BODY_TOP_OFFSET, width: columnWidth }}
        >
            {/* Inner container offset to align with time labels */}
            <div className='relative' style={{ top: BODY_TOP_OFFSET, height: totalHeight }}>
                {/* Slot gridlines */}
                {Array.from({ length: totalSlots }).map((_, i) => (
                    <div
                        key={i}
                        className='absolute left-0 w-full border-b border-gray-300'
                        style={{ top: i * config.slotHeight, height: config.slotHeight }}
                    />
                ))}

                {bgBands}

                {foregroundEvents.map((ev) => (
                    <KaliaCalendarEvent key={ev.id} event={ev} resources={allResources} />
                ))}
            </div>
        </div>
    );
}

export default React.memo(KaliaCalendarColumn);
